import { getHost } from '@repo/host/host';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectFilterState, selectFoundPins, selectLocationEnabled, selectMapCategories, selectMapData, selectMapSubcategories, selectPlayerCoords, selectPlayerLocationDeltas } from '@repo/store/maps/slices/mapData';
import type { MapDataCategory, MapDataGameCoordinateDeltas, MapDataLocationFeature, MapDataSubcategory, NearbyPinNotificationData, PlayerLocationDeltas } from '@repo/types/maps/map-types';
import { gameDistance2D, latLngToGameCoords, remapGameCoords } from '@repo/types/maps/map-utils';
import { getPinImageUrl } from '@repo/utility/image-urls';
import { useEffect, useRef } from 'react';
import { DEFAULT_NEARBY_DISTANCE_MERCATOR } from '../config/nearby-pin-notification';
interface NearbyPinState {
  type: 'idle' | 'single';
  externalId: string | null;
}
const IDLE_STATE: NearbyPinState = {
  type: 'idle',
  externalId: null
};
const IDLE_DEBOUNCE_MS = 10_000;
interface NearbyPinNotificationResult {
  state: NearbyPinState;
  rpcData: NearbyPinNotificationData;
}
export class NearbyPinNotificationDispatcher {
  readonly #send: (data: NearbyPinNotificationData) => void;
  readonly #idleDebounceMs: number;
  #state: NearbyPinState | null = null;
  #idleTimeout: ReturnType<typeof setTimeout> | null = null;
  public constructor(send: (data: NearbyPinNotificationData) => void, idleDebounceMs = IDLE_DEBOUNCE_MS) {
    this.#send = send;
    this.#idleDebounceMs = idleDebounceMs;
  }
  public update(result: NearbyPinNotificationResult): void {
    if (result.state.type === 'idle' && this.#state?.type === 'single') {
      this.#idleTimeout ??= setTimeout(() => {
        this.#idleTimeout = null;
        this.#apply(result);
      }, this.#idleDebounceMs);
      return;
    }
    this.#clearIdleTimeout();
    this.#apply(result);
  }
  public dispose(): void {
    this.#clearIdleTimeout();
  }
  #apply(result: NearbyPinNotificationResult): void {
    if (!hasStateChanged(this.#state, result.state)) return;
    this.#state = result.state;
    this.#send(result.rpcData);
  }
  #clearIdleTimeout(): void {
    if (this.#idleTimeout === null) return;
    clearTimeout(this.#idleTimeout);
    this.#idleTimeout = null;
  }
}
function findClosestPin(features: ReadonlyArray<MapDataLocationFeature>, playerCoords: {
  x: number;
  y: number;
}, deltas: PlayerLocationDeltas, filters: {
  teleportOnly: boolean;
  hiddenSubcategories: string[];
}, foundPins: Record<string, boolean>, categories: ReadonlyArray<MapDataCategory> | undefined, subcategories: ReadonlyArray<MapDataSubcategory> | undefined): MapDataLocationFeature | null {
  const deltasData = deltas.deltas;
  const gameCoordsMap = deltasData.gameCoordsMap;
  const avgScale = (Math.abs(deltasData.scaleX ?? 1) + Math.abs(deltasData.scaleY ?? 1)) / 2;
  const threshold = DEFAULT_NEARBY_DISTANCE_MERCATOR * avgScale;
  let closest: MapDataLocationFeature | null = null;
  let closestDist = Number.POSITIVE_INFINITY;
  for (const feature of features) {
    if (feature.geometry.type !== 'Point' || feature.geometry.coordinates.length < 2) continue;
    const pinCoords = getPinGameCoords(feature, deltasData, gameCoordsMap);
    if (!pinCoords) continue;
    const dist = gameDistance2D(playerCoords, pinCoords);
    if (dist > threshold) continue;
    if (foundPins[feature.properties.externalId]) continue;
    if (filters.teleportOnly && !feature.properties.gameCoords) continue;
    const sub = subcategories?.find(s => s.externalId === feature.properties.subcategoryExternalId);
    if (!sub || sub.excludeFromChecklists) continue;
    if (filters.hiddenSubcategories.includes(sub.externalId)) continue;
    const cat = categories?.find(c => c.externalId === sub.categoryExternalId);
    if (!cat || cat.excludeFromChecklists) continue;
    if (dist < closestDist) {
      closest = feature;
      closestDist = dist;
    }
  }
  return closest;
}
function getPinGameCoords(feature: MapDataLocationFeature, deltasData: MapDataGameCoordinateDeltas, gameCoordsMap: MapDataGameCoordinateDeltas['gameCoordsMap']): {
  x: number;
  y: number;
} | null {
  const gc = feature.properties.gameCoords;
  if (gc) {
    const remapped = remapGameCoords(gc, gameCoordsMap);
    return {
      x: remapped.x,
      y: remapped.y
    };
  }
  const [lng = 0, lat = 0] = feature.geometry.coordinates;
  return latLngToGameCoords(lng, lat, deltasData);
}
function buildNotificationData(closestPin: MapDataLocationFeature | null, subcategories: ReadonlyArray<MapDataSubcategory> | undefined): NearbyPinNotificationResult {
  if (!closestPin) {
    return {
      state: IDLE_STATE,
      rpcData: {
        type: 'idle'
      }
    };
  }
  const subcategory = subcategories?.find(s => s.externalId === closestPin.properties.subcategoryExternalId);
  const pinName = closestPin.properties.title || subcategory?.title || '';
  const categoryName = subcategory?.title ?? '';
  const externalId = closestPin.properties.externalId;
  if (!externalId || !pinName || !categoryName) {
    return {
      state: IDLE_STATE,
      rpcData: {
        type: 'idle'
      }
    };
  }
  return {
    state: {
      type: 'single',
      externalId
    },
    rpcData: {
      type: 'single',
      pinName,
      categoryName,
      description: closestPin.properties.description,
      imageUrl: getPinImageUrl(closestPin.properties.imageId, closestPin.properties.imagePath),
      externalId
    }
  };
}
function hasStateChanged(prev: NearbyPinState | null, current: NearbyPinState): boolean {
  return !prev || prev.type !== current.type || prev.externalId !== current.externalId;
}
export function useNearbyPinNotification(): void {
  const mapData = useAppSelector(selectMapData);
  const playerCoords = useAppSelector(selectPlayerCoords);
  const deltas = useAppSelector(selectPlayerLocationDeltas);
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const categories = useAppSelector(selectMapCategories);
  const subcategories = useAppSelector(selectMapSubcategories);
  const filters = useAppSelector(selectFilterState);
  const foundPins = useAppSelector(selectFoundPins);
  const dispatcherRef = useRef<NearbyPinNotificationDispatcher | null>(null);
  dispatcherRef.current ??= new NearbyPinNotificationDispatcher(data => getHost().sendNearbyPinNotification(data));
  useEffect(() => {
    const dispatcher = dispatcherRef.current;
    return () => dispatcher?.dispose();
  }, []);
  useEffect(() => {
    if (!mapData || !playerCoords || !deltas || !locationEnabled) {
      dispatcherRef.current?.update({
        state: IDLE_STATE,
        rpcData: {
          type: 'idle'
        }
      });
      return;
    }
    const closestPin = findClosestPin(mapData.geojson.features, playerCoords, deltas, filters, foundPins, categories, subcategories);
    const result = buildNotificationData(closestPin, subcategories);
    dispatcherRef.current?.update(result);
  }, [mapData, playerCoords, deltas, locationEnabled, categories, subcategories, filters, foundPins]);
}